import { useMemo, useState } from "react";

import clsx from "clsx";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import { getQueryParams } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import TextBoxWithCopyButton from "@shared/text-box-with-copy-button";

import { type Platform } from "../../constants";
import {
  getNorthbeamUtmPlatformParams,
  usePlatformsMetadata,
} from "../../metadata";

import { CopyButton } from "./copy-button";
import { SetupSection } from "./setup-section";
import { Code } from "./styled";

export const UtmParamsClipboard = ({
  platform,
  className,
  aggregateMultipleParams = true,
}: {
  platform: Platform;
  className?: string;
  aggregateMultipleParams?: boolean;
}) => {
  const urlParamPairs = getNorthbeamUtmPlatformParams(platform);

  const paramsValue = `${urlParamPairs
    .map(({ key, value }) => `${key}=${value}`)
    .join("&")}`;

  const individualParamsBlocks = urlParamPairs.map(({ key, value }) => (
    <p key={key} className="inline-flex items-center font-bold card-text">
      {key}
      <TextBoxWithCopyButton
        className="pl-4"
        text={value}
        textCopiedNotificationMessage={`${key} parameter copied!`}
      />
    </p>
  ));

  return (
    <div
      className={clsx(
        "flex flex-col w-full",
        aggregateMultipleParams ? "gap-4" : "gap-1",
        className,
      )}
    >
      {aggregateMultipleParams && (
        <>
          <div className="input-group">
            <Code block>{paramsValue}</Code>
          </div>
          <CopyButton value={paramsValue}>Copy script to clipboard</CopyButton>
        </>
      )}
      {!aggregateMultipleParams && <>{individualParamsBlocks}</>}
    </div>
  );
};

function encodeFullUrl(url: string): string {
  // Split the URL into its components
  const [baseUrl, queryString] = url.split("?");
  const encodedBaseUrl = encodeURI(baseUrl);
  if (!queryString) {
    return encodedBaseUrl;
  }
  // Encode query parameters separately
  const encodedQueryString = queryString
    .split("&")
    .map((param) => {
      const [key, value] = param.split("=");
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join("&");
  return `${encodedBaseUrl}?${encodedQueryString}`;
}

export const ConstructAndCopyUrl = ({
  platform,
  doNotEncodeUtmParams,
}: {
  platform: Platform;
  doNotEncodeUtmParams?: boolean;
}) => {
  const { addToast } = useToasts();
  const [destinationUrl, setDestinationUrl] = useState<string>("");
  const [constructedUrl, setConstructedUrl] = useState<string>("");
  const urlParamPairs = getNorthbeamUtmPlatformParams(platform);
  return (
    <>
      <div className="mb-0 control-label" data-testid="destination-url-label">
        Paste the destination URL for your ad here. Please include{" "}
        <Code>https://</Code>.
      </div>
      <input
        type="text"
        className="mb-0 form-control"
        data-testid="destination-url-input"
        value={destinationUrl}
        onChange={(event) => setDestinationUrl(event.target.value)}
      />
      <p className="mb-0">
        Construct and copy the new URL with Northbeam&apos;s URL parameters.
      </p>
      <PrimaryButton
        onClick={() => {
          try {
            if (doNotEncodeUtmParams) {
              const url = `${destinationUrl}?${urlParamPairs
                .map(({ key, value }) => `${key}=${value}`)
                .join("&")}`;
              setConstructedUrl(url);
              window.navigator.clipboard.writeText(url);
            } else {
              const newConstructedUrl = new URL(destinationUrl);
              urlParamPairs.forEach((urlParam) => {
                if (!newConstructedUrl.searchParams.has(urlParam.key)) {
                  newConstructedUrl.searchParams.append(
                    urlParam.key,
                    urlParam.value,
                  );
                }
              });

              const encodedConstructedUrl = encodeFullUrl(
                newConstructedUrl.toString(),
              );
              setConstructedUrl(encodedConstructedUrl);
              window.navigator.clipboard.writeText(encodedConstructedUrl);
            }

            addToast("Copied to clipboard!", { appearance: "success" });
          } catch (err) {
            let message = "Problem creating URL!";
            if (err instanceof Error) {
              message = err.message;
            }
            addToast(message, { appearance: "error" });
          }
        }}
        isDisabled={destinationUrl === ""}
        style={{ maxWidth: "fit-content" }}
      >
        CONSTRUCT AND COPY
      </PrimaryButton>
      <div className="input-group">
        <textarea
          rows={4}
          className="form-control"
          readOnly
          value={constructedUrl}
        />
      </div>
    </>
  );
};

export const ConstructDestinationUrl = ({
  platform,
}: {
  platform: Platform;
}) => {
  const { getTitleForPlatform } = usePlatformsMetadata();
  return (
    <>
      <SetupSection
        title={`Add UTM parameters to all destination URLs for new and existing ads in ${getTitleForPlatform(
          platform,
        )}`}
      >
        <UtmParamsClipboard platform={platform} />
      </SetupSection>
      <SetupSection
        title="Paste the destination URL - and copy the new URL with Northbeam’s URL
          parameters"
      >
        <ConstructAndCopyUrl platform={platform} />
      </SetupSection>
    </>
  );
};

export const URLVerification = ({ platform }: { platform: Platform }) => {
  const { user } = useUser();
  const { addToast } = useToasts();
  const [inputtedUrl, setInputtedUrl] = useState<string>("");

  const { getMetadata } = usePlatformsMetadata();

  const platformParams = getMetadata(platform).utmParams;
  const validateInputtedUrl = useMemo(
    () => () => {
      if (!platformParams) {
        return addToast(<></>);
      }

      let urlParams: URLSearchParams;
      try {
        urlParams = getQueryParams(inputtedUrl);
      } catch (e) {
        if (e instanceof Error && e.name === "InvalidUrlError") {
          return addToast(
            <p>
              URL is malformed and cannot be parsed. Did you forget to add{" "}
              <Code>https://</Code>?
            </p>,
            {
              appearance: "warning",
            },
          );
        } else {
          throw e;
        }
      }
      const duplicatedOrMissingParams: string[] = platformParams.reduce(
        (prev: string[], curr: any) => {
          const values = urlParams.getAll(curr.key);
          if (
            values.length !== 1 ||
            values[0] !== decodeURIComponent(curr.value)
          ) {
            return [...prev, curr.key];
          }
          return prev;
        },
        [],
      );
      if (duplicatedOrMissingParams.length > 0) {
        addToast(
          <p>
            The following param keys were not correctly added{" "}
            {duplicatedOrMissingParams.join(", ")}.
          </p>,
          {
            appearance: "warning",
          },
        );
      } else {
        addToast(<p>The URL appears to be correct.</p>, {
          appearance: "success",
        });
      }
    },
    [inputtedUrl, platformParams, addToast],
  );

  if (!user.isAdmin) {
    return null;
  }

  return (
    <SetupSection title="Verify your URL structure">
      <div className="flex flex-col gap-4">
        <div className="control-label" data-testid="url-label">
          <span>
            <i className="fa-regular fa-sharp fa-user" /> &nbsp;
          </span>{" "}
          Check your URL by pasting the full URL here with all your existing
          query params. Please include <Code>https://</Code>.
        </div>
        <input
          type="text"
          className="form-control"
          data-testid="url-input"
          value={inputtedUrl}
          onChange={(event) => setInputtedUrl(event.target.value)}
        />
        <PrimaryButton
          className="mt-1 mb-0 uppercase"
          style={{ maxWidth: "fit-content" }}
          onClick={validateInputtedUrl}
        >
          Verify
        </PrimaryButton>
      </div>
    </SetupSection>
  );
};
